import { useState, useCallback, useMemo } from 'react';
import { Nav } from '../components/Nav';
import { WeekCard } from '../components/WeekCard';
import { LoginModal } from '../components/LoginModal';
import { useScheduleData } from '../hooks/useScheduleData';
import { useAuth } from '../hooks/useAuth';
import { isWeekInFuture } from '../utils/date';

export function SchedulePage() {
  const {
    data,
    hymns,
    loading,
    error,
    updateWeek,
    updateSong,
    addWeek,
    deleteWeek,
    addSong,
    removeSong,
    save,
    getHymnDisplayText,
    getPianistName,
    getConductorName,
  } = useScheduleData();

  const { isScheduleAuthenticated, loginForSchedule, isAdminMode } = useAuth();

  const [showLoginModal, setShowLoginModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const [showPastWeeks, setShowPastWeeks] = useState(false);
  const [allExpanded, setAllExpanded] = useState(false);
  const [actionsCollapsed, setActionsCollapsed] = useState(true);

  const requireAuth = useCallback(
    (action: () => void) => {
      if (isScheduleAuthenticated) {
        action();
      } else {
        setPendingAction(() => action);
        setShowLoginModal(true);
      }
    },
    [isScheduleAuthenticated]
  );

  const handleLoginSuccess = useCallback(
    async (password: string) => {
      const success = await loginForSchedule(password);
      if (success && pendingAction) {
        pendingAction();
        setPendingAction(null);
      }
      return success;
    },
    [loginForSchedule, pendingAction]
  );

  const handleCloseModal = useCallback(() => {
    setShowLoginModal(false);
    setPendingAction(null);
  }, []);

  const handleSave = useCallback(async () => {
    const success = await save();
    if (success) {
      alert('Schedule saved successfully!');
    } else {
      alert('Failed to save schedule');
    }
  }, [save]);

  const togglePastWeeks = useCallback(() => {
    setShowPastWeeks((prev) => !prev);
  }, []);

  const toggleExpandAll = useCallback(() => {
    setAllExpanded((prev) => !prev);
  }, []);

  const toggleActionsMenu = useCallback(() => {
    setActionsCollapsed((prev) => !prev);
  }, []);

  const filteredWeeks = useMemo(() => {
    if (!data) return [];
    return data.weeks
      .map((week, index) => ({ week, originalIndex: index }))
      .filter(({ week }) => showPastWeeks || isWeekInFuture(week.date));
  }, [data, showPastWeeks]);

  if (loading) {
    return (
      <>
        <Nav />
        <div className="page-container">
          <h1>Church Music Schedule</h1>
          <p>Loading...</p>
        </div>
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <Nav />
        <div className="page-container">
          <h1>Church Music Schedule</h1>
          <p style={{ color: 'red' }}>Error: {error || 'Failed to load data'}</p>
        </div>
      </>
    );
  }

  return (
    <>
      <Nav />
      <LoginModal
        isOpen={showLoginModal}
        onClose={handleCloseModal}
        onLogin={handleLoginSuccess}
        title="Enter Password to Edit"
        description="You need to log in to make changes to the schedule."
      />

      <div className="page-container">
        <div className="page-header">
          <h1>Church Music Schedule</h1>

          <div
            className={`actions-section ${
              actionsCollapsed ? 'actions-section--collapsed' : ''
            }`}
          >
            <button
              className="actions-toggle btn-secondary"
              onClick={toggleActionsMenu}
            >
              <span>Actions</span>
              <span className="actions-toggle__icon">V</span>
            </button>
            <div
              className="actions-bar"
              style={{ justifyContent: 'space-between' }}
            >
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button
                  className={`btn-primary ${
                    !isScheduleAuthenticated ? 'edit-disabled' : ''
                  }`}
                  onClick={() => requireAuth(addWeek)}
                >
                  + Add Week
                </button>
                <button
                  className={`btn-success ${
                    !isScheduleAuthenticated ? 'edit-disabled' : ''
                  }`}
                  onClick={() => requireAuth(handleSave)}
                >
                  Save All Changes
                </button>
              </div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button className="btn-secondary" onClick={toggleExpandAll}>
                  {allExpanded ? 'Collapse All' : 'Expand All'}
                </button>
                <button className="btn-secondary" onClick={togglePastWeeks}>
                  {showPastWeeks ? 'Hide Past Weeks' : 'Show Past Weeks'}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="weeks-scroll-container">
          <div id="weeks-container">
            {filteredWeeks.map(({ week, originalIndex }) => (
              <WeekCard
                key={week.id}
                week={week}
                weekIndex={originalIndex}
                pianists={data.pianists}
                conductors={data.conductors}
                hymns={hymns}
                getHymnDisplayText={getHymnDisplayText}
                getPianistName={getPianistName}
                getConductorName={getConductorName}
                onUpdateWeek={(field, value) => {
                  requireAuth(() => updateWeek(originalIndex, field, value));
                }}
                onUpdateSong={(songIndex, field, value) => {
                  requireAuth(() =>
                    updateSong(originalIndex, songIndex, field, value)
                  );
                }}
                onAddSong={() => requireAuth(() => addSong(originalIndex))}
                onRemoveSong={(songIndex) =>
                  requireAuth(() => removeSong(originalIndex, songIndex))
                }
                onDeleteWeek={
                  isAdminMode
                    ? () => requireAuth(() => deleteWeek(originalIndex))
                    : undefined
                }
                isAdminMode={isAdminMode}
                disabled={!isScheduleAuthenticated}
                onAuthRequired={!isScheduleAuthenticated ? () => requireAuth(() => {}) : undefined}
                initiallyExpanded={allExpanded}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
